/**
 * useDetailPanelState — Reusable hook for the generic slide-over/modal/fullscreen
 * detail panel with URL sync (?detail=DOC_NAME).
 *
 * Matches the Item page's behaviour:
 *  - Initialize from URL on mount
 *  - Push ?detail= when panel opens (browser back closes it)
 *  - Replace ?detail= when switching items (no history pollution)
 *  - Remove ?detail= when panel closes via UI
 *  - Close panel when browser back removes ?detail=
 *  - Focus management: restore row focus on close
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export interface DetailPanelItem {
  name: string;
  label?: string;
}

export function useDetailPanelState() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize from URL so refresh / shared link reopens the panel
  const [detailPanelItem, setDetailPanelItem] = useState<DetailPanelItem | null>(() => {
    const initial = new URLSearchParams(window.location.search).get("detail");
    return initial ? { name: initial } : null;
  });

  // Keep last item for crossfade (Framer Motion AnimatePresence)
  const lastDetailItemRef = useRef<DetailPanelItem | null>(detailPanelItem);
  if (detailPanelItem) lastDetailItemRef.current = detailPanelItem;
  const panelItem = detailPanelItem ?? lastDetailItemRef.current;

  // --- URL sync ---
  const prevDetailRef = useRef<string | null>(detailPanelItem?.name ?? null);
  const suppressUrlSyncRef = useRef(false);

  useEffect(() => {
    if (suppressUrlSyncRef.current) {
      suppressUrlSyncRef.current = false;
      prevDetailRef.current = detailPanelItem?.name ?? null;
      return;
    }
    const curName = detailPanelItem?.name ?? null;
    const prevName = prevDetailRef.current;
    prevDetailRef.current = curName;

    if (curName && !prevName) {
      // Panel opened — push so browser back closes it
      setSearchParams((prev) => { prev.set("detail", curName); return prev; });
    } else if (curName && prevName && curName !== prevName) {
      // Item switched — replace (no history pollution)
      setSearchParams((prev) => { prev.set("detail", curName); return prev; }, { replace: true });
    } else if (!curName && prevName) {
      // Panel closed via UI — replace to remove param
      setSearchParams((prev) => { prev.delete("detail"); return prev; }, { replace: true });
    }
  }, [detailPanelItem, setSearchParams]);

  // Browser back detection: if URL param removed externally, close panel
  useEffect(() => {
    const urlDetail = searchParams.get("detail");
    if (!urlDetail && detailPanelItem) {
      suppressUrlSyncRef.current = true;
      setDetailPanelItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // --- Focus management ---
  const lastPanelRowRef = useRef<string | null>(null);
  useEffect(() => {
    if (detailPanelItem) {
      lastPanelRowRef.current = detailPanelItem.name;
    } else if (lastPanelRowRef.current) {
      const rowEl = document.querySelector(
        `[data-row-id="${CSS.escape(lastPanelRowRef.current)}"]`,
      ) as HTMLElement | null;
      rowEl?.focus();
      lastPanelRowRef.current = null;
    }
  }, [detailPanelItem]);

  const openDetailPanel = useCallback((name: string, label?: string) => {
    setDetailPanelItem({ name, label });
  }, []);

  const closeDetailPanel = useCallback(() => {
    setDetailPanelItem(null);
  }, []);

  return {
    /** Current panel item (null when closed) */
    detailPanelItem,
    /** For AnimatePresence crossfade — keeps last item during exit animation */
    panelItem,
    /** Open the panel for a specific doc */
    openDetailPanel,
    /** Close the panel */
    closeDetailPanel,
    /** Raw setter for advanced use (e.g., auto-close on filter change) */
    setDetailPanelItem,
  };
}
